import React from 'react'
import ReportWrapper from '../ReportWrapper'
import ReportSectionHeading from '../ReportSectionHeading'
import CompanyBankApplicationRecordsOverview from './CompanyBankApplicationRecordsOverview'
import CompanyBankApplicationRecordsChart from './CompanyBankApplicationRecordsChart'
import { Stack, Table, Text } from '@mantine/core'
import { useTheme } from '../../../components/ThemeProvider'
import { useViewMode } from '../ViewModeProvider'

interface CompanyBankApplicationRecordsProps {
  data: any
  title: string
  sectionIndex?: string
}

const applicationRangeMap: Record<string, string>[] = [
  { label: '近7天', keyCount: 'b_d7_apply_count', keyOrg: 'b_d7_apply_org_count' },
  { label: '近15天', keyCount: 'b_d15_apply_count', keyOrg: 'b_d15_apply_org_count' },
  { label: '近1月', keyCount: 'b_m1_apply_count', keyOrg: 'b_m1_apply_org_count' },
  { label: '近3月', keyCount: 'b_m3_apply_count', keyOrg: 'b_m3_apply_org_count' },
  { label: '近6月', keyCount: 'b_m6_apply_count', keyOrg: 'b_m6_apply_org_count' },
  { label: '近1年', keyCount: 'b_m12_apply_count', keyOrg: 'b_m12_apply_org_count' },
]

const institutionTypes = [
  { label: '全国性银行', countKey: 'b_{period}_apply_tra_count', orgKey: 'b_{period}_apply_tra_org_count' },
  { label: '区域银行', countKey: 'b_{period}_apply_reg_count', orgKey: 'b_{period}_apply_reg_org_count' },
  { label: '网络零售银行', countKey: 'b_{period}_apply_ret_count', orgKey: 'b_{period}_apply_ret_org_count' },
]

const getValue = (data: any, key: string) => {
  const value = data?.[key]
  if (value === null || value === undefined) return '0'
  if (typeof value === 'number') return value.toString()
  return value
}

export default function CompanyBankApplicationRecords({
  data,
  title,
  sectionIndex,
}: CompanyBankApplicationRecordsProps) {
  const theme = useTheme()
  const { viewMode } = useViewMode()

  return (
    <ReportWrapper>
      <ReportSectionHeading title={title} index={sectionIndex} />
      <CompanyBankApplicationRecordsOverview data={data} />
      <div className="mt-2">
        <Text size="sm" fw={600} style={{ color: theme.accentTextColor }}>
          申请明细
        </Text>
        {viewMode === 'table' ? (
          <Stack gap="md" className="mt-3">
            {applicationRangeMap.map((period) => (
              <div key={period.label}>
                <Text size="xs" fw={600} className="text-gray-600 mb-2">
                  {period.label}
                </Text>
                <Table
                  highlightOnHover
                  verticalSpacing="sm"
                  withColumnBorders={false}
                  styles={{
                    table: { fontSize: '13px', tableLayout: 'fixed' },
                    th: {
                      backgroundColor: theme.surfaceMutedColor,
                      color: theme.accentTextColor,
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: '40%' }}>机构类别</Table.Th>
                      <Table.Th style={{ width: '30%' }}>申请次数</Table.Th>
                      <Table.Th style={{ width: '30%' }}>申请机构数</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {institutionTypes.map((type) => {
                      const prefix = period.keyCount.split('_')[1]
                      const countKey = type.countKey.replace('{period}', prefix)
                      const orgKey = type.orgKey.replace('{period}', prefix)

                      return (
                        <Table.Tr key={`${period.label}-${type.label}`}>
                          <Table.Td>{type.label}</Table.Td>
                          <Table.Td>{getValue(data, countKey)}</Table.Td>
                          <Table.Td>{getValue(data, orgKey)}</Table.Td>
                        </Table.Tr>
                      )
                    })}
                  </Table.Tbody>
                </Table>
              </div>
            ))}
          </Stack>
        ) : (
          <div className="mt-3">
            <CompanyBankApplicationRecordsChart data={data} />
          </div>
        )}
      </div>
    </ReportWrapper>
  )
}
